import { FormEvent, ReactNode } from 'react';
import { useLang } from 'hooks/lang';

import { Column, Row, Label, Input, RadioInput, Checkbox } from 'components/Blocks';

import { useFormControl } from 'hooks';

import { TeamFormData, booleanRenders, groupRenders } from './utils';
import { cn } from 'styles';

interface TeamFormProps {
  children: ReactNode;
  data: TeamFormData;
  onSubmit: (team: TeamFormData) => void;
  disabled?: boolean;
}

const NULL_TOKEN = '**null';

function TeamForm({ data, onSubmit, disabled, children }: TeamFormProps) {
  const { t } = useLang();

  const control = useFormControl(data, { watch: true });

  const handleSubmit = (formEvent: FormEvent): void => {
    formEvent.preventDefault();

    onSubmit(control.state);
  };

  return (
    <Column
      as="form"
      gap="8"
      className="mx-auto max-w-[960px] rounded-lg border p-5"
      onSubmit={handleSubmit}
    >
      <Column gap="2">
        <Label>{t('name')}</Label>

        <Input
          required
          name="name"
          disabled={disabled}
          value={control.state.name}
          onChange={control.handleChange}
        />
      </Column>

      {groupRenders.map(({ getLabel, property, values }) => (
        <Row key={property} justifyContent="space-between" className="phone:flex-col">
          <Label className="text-base font-normal" htmlFor={property}>
            {getLabel(t)}
          </Label>

          <RadioInput
            orientation="horizontal"
            name={property}
            disabled={disabled}
            onChangeDirect={(value) =>
              control.dispatch({ [property]: value === NULL_TOKEN ? null : value })
            }
            value={
              control.state[property] == null ? NULL_TOKEN : (control.state[property] as string)
            }
            options={values.map(({ value, getLabel }) => ({
              label: getLabel(t),
              value: value === null ? NULL_TOKEN : value,
            }))}
            render={({ label, selected }) => (
              <div
                className={cn(
                  'rounded-lg border px-4 py-2 text-sm font-medium',
                  selected ? 'border-primary text-primary' : !disabled && 'hover:bg-hover',
                )}
              >
                {label}
              </div>
            )}
          />
        </Row>
      ))}

      <div className="mx-auto grid w-[80%] grid-cols-3 gap-4 small_tablet:grid-cols-1 phone:w-full">
        {booleanRenders.map(({ property, getLabel }) => (
          <Row key={property} gap="2">
            <Checkbox
              checked={control.state[property] as boolean}
              onChange={({ target }) => control.dispatch({ [property]: target.checked })}
              disabled={disabled}
              id={property}
            />

            <Label htmlFor={property} className="font-normal">
              {getLabel(t)}
            </Label>
          </Row>
        ))}
      </div>

      {children}
    </Column>
  );
}

export default TeamForm;
